/**
 * /performance/goals — the goal-cascade UI (ADR-0013 Phase 4). NET-NEW screen,
 * no precedent in the codebase.
 *
 * Approach (flagged in the PR): a single cycle-scoped view with (1) two compact
 * create forms — org objectives (owner/admin) and employee KRAs (manager-of /
 * HR proposes, with a weight and an optional parent link) — and (2) an
 * "alignment view" that renders the goals as a tree by `parentGoalId`, so you
 * can see which org objective a KRA cascades from. The per-owner weight-sum
 * warning is surfaced inline; the activation gate is the server's job (it blocks
 * goal_setting → active when weights ≠ 100), so this screen only WARNS.
 */
import { createRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { listEmployees } from '../api/employees.js';
import {
  createGoal,
  deleteGoal,
  listCycleGoals,
  listCycles,
  type PerfGoal,
  type WeightWarning,
} from '../api/performance.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';

export const performanceGoalsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/performance/goals',
  component: PerformanceGoalsPage,
});

function PerformanceGoalsPage(): JSX.Element {
  const cycles = useQuery({ queryKey: ['perf', 'cycles', 'goal_setting'], queryFn: () => listCycles('goal_setting') });
  const [cycleId, setCycleId] = useState<string>('');
  const activeCycle = cycleId || cycles.data?.cycles[0]?.id || '';

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Goal cascade</h1>
        <p className="text-sm text-gray-400">
          Set org objectives, propose employee KRAs with weights, and see how each KRA aligns up the tree.
          Goals are editable only while the cycle is in <span className="text-gray-200">goal setting</span>.
        </p>
      </header>

      {cycles.isLoading ? <Placeholder>Loading…</Placeholder> : (cycles.data?.cycles ?? []).length === 0 ? (
        <Placeholder>No cycle is in goal-setting. Move a cycle to <span className="text-gray-200">goal_setting</span> in Performance administration first.</Placeholder>
      ) : (
        <>
          <label className="block space-y-1 max-w-sm">
            <span className="text-xs text-gray-400">Cycle</span>
            <select value={activeCycle} onChange={(e) => setCycleId(e.target.value)} className={inputCls}>
              {(cycles.data?.cycles ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {activeCycle ? <CycleGoals cycleId={activeCycle} /> : null}
        </>
      )}
    </section>
  );
}

function CycleGoals({ cycleId }: { cycleId: string }): JSX.Element {
  const qc = useQueryClient();
  const goalsQ = useQuery({ queryKey: ['perf', 'goals', cycleId], queryFn: () => listCycleGoals(cycleId) });
  const employeesQ = useQuery({ queryKey: ['employees', 'all'], queryFn: () => listEmployees({ limit: 200 }) });
  const refresh = (): Promise<void> => qc.invalidateQueries({ queryKey: ['perf', 'goals', cycleId] });

  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of employeesQ.data?.employees ?? []) map.set(e.id, `${e.firstName} ${e.lastName}`);
    return (id: string | null): string => (id ? map.get(id) ?? id : '—');
  }, [employeesQ.data]);

  const goals = goalsQ.data?.goals ?? [];
  const warnings = goalsQ.data?.weightWarnings ?? [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <OrgObjectiveForm cycleId={cycleId} onCreated={() => void refresh()} />
        <KraForm cycleId={cycleId} goals={goals} employees={employeesQ.data?.employees ?? []} onCreated={() => void refresh()} />
      </div>

      {warnings.length > 0 ? (
        <div className="rounded border border-yellow-900 bg-yellow-950/40 p-3 text-xs text-yellow-300">
          <p className="font-medium">Weight warnings (activation will be blocked until these total 100%):</p>
          <ul className="mt-1 space-y-0.5">
            {warnings.map((w: WeightWarning) => (
              <li key={w.ownerEmployeeId}>{nameOf(w.ownerEmployeeId)} — {w.weightSum}%</li>
            ))}
          </ul>
        </div>
      ) : null}

      {goalsQ.isLoading ? <Placeholder>Loading goals…</Placeholder> : (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-gray-500">Alignment</p>
          {goals.length === 0 ? <Placeholder>No goals yet.</Placeholder> : (
            <GoalTree goals={goals} nameOf={nameOf} onDeleted={() => void refresh()} />
          )}
        </div>
      )}
    </div>
  );
}

/** Render the goals as a tree by parentGoalId. Roots = goals with no parent. */
function GoalTree({ goals, nameOf, onDeleted }: { goals: PerfGoal[]; nameOf: (id: string | null) => string; onDeleted: () => void }): JSX.Element {
  const childrenOf = useMemo(() => {
    const m = new Map<string | null, PerfGoal[]>();
    for (const g of goals) {
      const k = g.parentGoalId;
      m.set(k, [...(m.get(k) ?? []), g]);
    }
    return m;
  }, [goals]);

  const render = (parentId: string | null, depth: number): JSX.Element[] =>
    (childrenOf.get(parentId) ?? []).map((g) => (
      <div key={g.id} style={{ marginLeft: depth * 20 }}>
        <GoalNode goal={g} nameOf={nameOf} onDeleted={onDeleted} />
        {render(g.id, depth + 1)}
      </div>
    ));

  return <div className="space-y-1">{render(null, 0)}</div>;
}

function GoalNode({ goal, nameOf, onDeleted }: { goal: PerfGoal; nameOf: (id: string | null) => string; onDeleted: () => void }): JSX.Element {
  const delMut = useMutation({
    mutationFn: () => deleteGoal(goal.id),
    onSuccess: onDeleted,
  });
  const isOrg = goal.ownerType === 'organization';
  return (
    <div className="rounded border border-gray-900 p-3 flex items-center justify-between gap-3">
      <div>
        <p className="text-sm text-white">
          {isOrg ? <span className="text-xs uppercase tracking-wide text-gray-500 mr-2">Org</span> : null}
          {goal.title}
          {goal.weight != null ? <span className="text-gray-500 text-xs ml-2">{Number(goal.weight)}%</span> : null}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          {isOrg ? 'organization objective' : `owner: ${nameOf(goal.ownerEmployeeId)}`} · {goal.status}
        </p>
      </div>
      {goal.status === 'draft' ? (
        <button type="button" onClick={() => delMut.mutate()} disabled={delMut.isPending} className={secondaryBtn} aria-label={`Delete ${goal.title}`}>Delete</button>
      ) : <span className="text-[0.65rem] uppercase tracking-wide text-green-400">agreed</span>}
      <ApiErrorNotice error={delMut.error} fallback="Delete failed." compact />
    </div>
  );
}

function OrgObjectiveForm({ cycleId, onCreated }: { cycleId: string; onCreated: () => void }): JSX.Element {
  const [title, setTitle] = useState('');
  const mut = useMutation({
    mutationFn: () => createGoal(cycleId, { ownerType: 'organization', title }),
    onSuccess: () => { setTitle(''); onCreated(); },
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); mut.mutate(); }} className="rounded border border-gray-900 p-4 space-y-2">
      <p className="text-sm text-white">Org objective <span className="text-xs text-gray-500">(owner/admin)</span></p>
      <input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Grow revenue 30%" className={inputCls} />
      <button type="submit" disabled={mut.isPending} className={primaryBtn}>{mut.isPending ? 'Adding…' : 'Add objective'}</button>
      <ApiErrorNotice error={mut.error} fallback="Could not create objective." compact />
    </form>
  );
}

function KraForm({ cycleId, goals, employees, onCreated }: {
  cycleId: string;
  goals: PerfGoal[];
  employees: Array<{ id: string; firstName: string; lastName: string }>;
  onCreated: () => void;
}): JSX.Element {
  const [ownerEmployeeId, setOwner] = useState('');
  const [title, setTitle] = useState('');
  const [weight, setWeight] = useState('');
  const [parentGoalId, setParent] = useState('');
  const mut = useMutation({
    mutationFn: () => createGoal(cycleId, {
      ownerType: 'employee', ownerEmployeeId, title,
      ...(weight ? { weight: Number(weight) } : {}),
      ...(parentGoalId ? { parentGoalId } : {}),
    }),
    onSuccess: () => { setTitle(''); setWeight(''); onCreated(); },
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); mut.mutate(); }} className="rounded border border-gray-900 p-4 space-y-2">
      <p className="text-sm text-white">Propose KRA <span className="text-xs text-gray-500">(manager / HR)</span></p>
      <select value={ownerEmployeeId} onChange={(e) => setOwner(e.target.value)} required className={inputCls}>
        <option value="">— owner —</option>
        {employees.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
      </select>
      <input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Ship feature X" className={inputCls} />
      <div className="flex gap-2">
        <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="weight %" min="0" max="100" className={`${inputCls} w-1/3`} />
        <select value={parentGoalId} onChange={(e) => setParent(e.target.value)} className={`${inputCls} flex-1`}>
          <option value="">— no parent —</option>
          {goals.map((g) => <option key={g.id} value={g.id}>{g.ownerType === 'organization' ? '[Org] ' : ''}{g.title}</option>)}
        </select>
      </div>
      <button type="submit" disabled={mut.isPending} className={primaryBtn}>{mut.isPending ? 'Proposing…' : 'Propose KRA'}</button>
      <ApiErrorNotice error={mut.error} fallback="Could not propose KRA." compact />
    </form>
  );
}

function Placeholder({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="rounded border border-gray-900 p-6 text-center text-sm text-gray-500">{children}</div>;
}

const inputCls = 'w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm';
const primaryBtn = 'rounded bg-green-700 text-white text-xs px-3 py-1.5 disabled:opacity-50';
const secondaryBtn = 'rounded border border-gray-700 text-xs px-3 py-1.5 hover:bg-gray-900';
